import { Guard } from '../async/async';
import type { Logger } from '../types/logger';


export function unorderedTaskExecution<T, R>(
  executor: (val: T) => Promise<R>,
  concurrencyStrategy: QueuingStrategy<T>,
  logger?: Logger,
  name?: string,
  flushCallback?: () => Promise<void>,
): TransformStream<T, R> {
  let waterLevel = 0;
  const guard = new Guard();
  let i = 0;
  let startTimestamp = 0;
  const ongoing: Record<number, Promise<unknown>> = {};
  const highWaterMark = Math.max(concurrencyStrategy.highWaterMark ?? 1, 1);
  const sizeFunc = concurrencyStrategy.size ?? (() => 1);
  return new TransformStream<T, R>({
    async transform(taskDesc, controller) {
      if (startTimestamp === 0) {
        startTimestamp = Date.now();
      }
      while (waterLevel >= highWaterMark) {
        await guard.wait();
      }
      const taskSize = sizeFunc(taskDesc);
      waterLevel += taskSize;
      const taskId = i++;
      const task = executor(taskDesc).then(result => {
        controller.enqueue(result);
      }).catch(ex => {
        controller.error(ex);
      }).finally(() => {
        delete ongoing[taskId];
        waterLevel -= taskSize;
        guard.notify();
      });
      ongoing[taskId] = task;
    },
    async flush() {
      await Promise.all(Object.values(ongoing));
      if (flushCallback) {
        await flushCallback();
      }
      const endTimestamp = Date.now();
      logger?.info(`${name} unorderedTaskExecution duration: ${endTimestamp - startTimestamp} ms (start: ${startTimestamp} ms, end: ${endTimestamp} ms)`);
    }
  });
}
